import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import METRIC_LOCALES_EN from '@intelligence/_locales/en-US/metricSubValue';
import METRIC_LOCALES_CN from '@intelligence/_locales/zh-CN/metricSubValue';

import type { IAlineaCalibrationFilterValues, IAlineaCalibrationPaging, IAlineaCalibrationSortState } from './types';

export const ALINEA_CALIBRATION_PAGE_ID = 'PCConsoleAlineaAnalyticsCalibration';
export const ALINEA_CALIBRATION_PAGE_LOG_KEY = 'topChartsPCConsoleGamesAlineaAnalyticsCalibration';
export const ALINEA_CALIBRATION_FILTER_SAVE_TYPE = 'alinea-analytics-calibration-pc-console-topCharts';
export const ALINEA_CALIBRATION_BUSINESS_TYPE = 'alinea-analytics-calibration-pc-console-topCharts';
export const ALINEA_CALIBRATION_DOWNLOAD_KEY = 'alineaAnalyticsCalibrationPCConsoleTopCharts';
export const ALINEA_CALIBRATION_COND_KEY = 'alineaCalibrationRegionList';
export const ALINEA_CALIBRATION_REGION_TARGET = 'alinea_calibration_region_list';
export const ALINEA_CALIBRATION_PAGE_SIZE_OPTIONS = [50, 100, 200];
export const ALINEA_CALIBRATION_MAX_DOWNLOAD_ROWS = 100000;
export const DEFAULT_PAGING: IAlineaCalibrationPaging = {
  page: 1,
  page_size: ALINEA_CALIBRATION_PAGE_SIZE_OPTIONS[0],
};

export const ALINEA_REGION_NAME_MAP: Record<string, { text: string; textCN: string; }> = {
  america: {
    text: 'The Americas',
    textCN: '美洲',
  },
  asia: {
    text: 'Asia',
    textCN: '亚洲',
  },
  europe: {
    text: 'Europe',
    textCN: '欧洲',
  },
};

/** platform 为 all 时，首行展开展示的四大平台图标 */
export const ALINEA_ALL_PLATFORM_ICONS = ['steam', 'ps', 'xbox', 'nintendo'];

export const ALINEA_PLATFORM_NAME_MAP: IRecord<string> = {
  all: 'All',
  console: 'Console',
  dreamcast: 'Dreamcast',
  epic: 'Epic',
  'game boy': 'Game Boy',
  'game boy advance': 'Game Boy Advance',
  'gog.com': 'GOG.com',
  'meta quest 2': 'Meta Quest 2',
  nintendo: 'Nintendo',
  'new nintendo 3ds': 'New Nintendo 3DS',
  'nintendo 3ds': 'Nintendo 3DS',
  'nintendo 64': 'Nintendo 64',
  'nintendo ds': 'Nintendo DS',
  'nintendo gamecube': 'Nintendo GameCube',
  'nintendo switch': 'Nintendo Switch',
  pc: 'PC',
  'pc (microsoft windows)': 'PC (Microsoft Windows)',
  ps: 'PlayStation',
  playstation: 'PlayStation',
  'playstation 2': 'PlayStation 2',
  'playstation 3': 'PlayStation 3',
  'playstation 4': 'PlayStation 4',
  'playstation 5': 'PlayStation 5',
  'playstation portable': 'PlayStation Portable',
  'playstation vr2': 'PlayStation VR2',
  'sega mega drive/genesis': 'SEGA Mega Drive/Genesis',
  steam: 'Steam',
  'super famicom': 'Super Famicom',
  'total playstation': 'Total PlayStation',
  'total xbox': 'Total Xbox',
  wii: 'Wii',
  'wii u': 'Wii U',
  xbox: 'Xbox',
  'xbox 360': 'Xbox 360',
  'xbox one': 'Xbox One',
  'xbox series x|s': 'Xbox Series X|S',
};

export const ALINEA_CALIBRATION_METRICS = [
  {
    label: METRIC_LOCALES_EN.dbi_metric_combine_alinea_analytics_calibration_lifetime_units_revenue,
    labelCN: METRIC_LOCALES_CN.dbi_metric_combine_alinea_analytics_calibration_lifetime_units_revenue,
    value: 'alinea_calibration__revenue',
  },
  {
    label: METRIC_LOCALES_EN.dbi_metric_combine_alinea_analytics_calibration_lifetime_units,
    labelCN: METRIC_LOCALES_CN.dbi_metric_combine_alinea_analytics_calibration_lifetime_units,
    value: 'alinea_calibration__copies_sold',
  },
  {
    label: METRIC_LOCALES_EN.dbi_metric_combine_alinea_analytics_calibration_total_players,
    labelCN: METRIC_LOCALES_CN.dbi_metric_combine_alinea_analytics_calibration_total_players,
    value: 'alinea_calibration__register_players',
  },
  {
    label: METRIC_LOCALES_EN.dbi_metric_combine_alinea_analytics_calibration_lifetime_wishlist,
    labelCN: METRIC_LOCALES_CN.dbi_metric_combine_alinea_analytics_calibration_lifetime_wishlist,
    value: 'alinea_calibration__wishlist',
  },
  {
    label: METRIC_LOCALES_EN.dbi_metric_combine_alinea_analytics_calibration_budget,
    labelCN: METRIC_LOCALES_CN.dbi_metric_combine_alinea_analytics_calibration_budget,
    value: 'alinea_calibration__budget',
  },
  {
    label: METRIC_LOCALES_EN.dbi_metric_combine_alinea_analytics_calibration_budget_development,
    labelCN: METRIC_LOCALES_CN.dbi_metric_combine_alinea_analytics_calibration_budget_development,
    value: 'alinea_calibration__budget_dev',
  },
  {
    label: METRIC_LOCALES_EN.dbi_metric_combine_alinea_analytics_calibration_budget_marketing,
    labelCN: METRIC_LOCALES_CN.dbi_metric_combine_alinea_analytics_calibration_budget_marketing,
    value: 'alinea_calibration__budget_mar',
  },
];

export const PUBLISHER_CLASS_OPTIONS = [
  {
    text: 'Hobbyist',
    textCN: '个人及爱好者发行',
    value: 'Hobbyist',
  },
  {
    text: 'Indie',
    textCN: '独立发行',
    value: 'Indie',
  },
  {
    text: 'AA',
    textCN: 'AA级发行',
    value: 'AA',
  },
  {
    text: 'AAA',
    textCN: '3A级发行',
    value: 'AAA',
  },
];

export const SOURCE_QUALITY_OPTIONS = [
  { text: 'Official', textCN: '官方', value: 'official' },
  { text: 'Secondary', textCN: '待核验', value: 'secondary' },
  { text: 'Estimate', textCN: '估算', value: 'estimate' },
];

export const DEFAULT_FILTER_VALUES: IAlineaCalibrationFilterValues = {
  rangeTime: {
    granularity: 'daily',
    startDate: '',
    endDate: '',
  },
  metrics: ALINEA_CALIBRATION_METRICS.map((item) => {
    return item.value;
  }),
  region: [],
  genre: {
    source: GENRE_SOURCE_MAP.IEGG,
    genres: [],
  },
  games: {
    value: [],
    keywords: '',
    isSearchAll: false,
    checkedList: [],
  },
  sourceQuality: SOURCE_QUALITY_OPTIONS.map((item) => {
    return item.value;
  }),
  releaseTime: {
    granularity: 'daily',
    startDate: '',
    endDate: '',
  },
  publisher: [],
  publisherClass: PUBLISHER_CLASS_OPTIONS.map((item) => {
    return item.value;
  }),
  publisherRegion: ['global'],
  developer: [],
};

export const DEFAULT_SORT_STATE: IAlineaCalibrationSortState = {
  order_metric: 'date',
  order: 'desc',
};
